import { useState, useEffect, useRef } from 'react';
import { useSitemap } from 'wsm-common-data';
import { useShowMapEvent } from './useShowMapEvent';
import { buildLocationTiles } from '../utils/buildLocationTiles';
import { useLocationsData } from './useLocationsData';
import { getDealerPhotoBaseUrl } from '../utils/getDealerPhotoBaseUrl';

const DEFAULT_RANGE = 50;
const DEFAULT_EXCLUDE_BY_RANGE = true;

export const useLocationSearch = (paginationData) => {
	const [range, setRange] = useState(DEFAULT_RANGE);
	const prevRangeRef = useRef(DEFAULT_RANGE);
	const showMap = useShowMapEvent('showMap', false);
	const dealerPhotoBaseUrl = getDealerPhotoBaseUrl();

	const {
		locations: allLocationData,
		locationCount,
		error: errorMsg,
		isLoading,
		isReady,
		setSearchParams
	} = useLocationsData();

	// Trigger search when postal code or range changes
	useEffect(() => {
		if (!isReady) {
			return; // Skip if not ready
		}
		if (prevRangeRef.current === range) {
			return; // Skip if range hasn't changed (prevents duplicate searches)
		}
		prevRangeRef.current = range;
		const postalCode = window.DDC.LocationsData.getPostalCode();
		if (postalCode) {
			setSearchParams(postalCode, range, DEFAULT_EXCLUDE_BY_RANGE);
		}
	}, [range, isReady, setSearchParams]);

	// Sync range from external widgets (e.g. ws-locations-facets) via
	// ws-locations-data pubsub.  When another widget changes distance via
	// setSearchParams, LocationsData publishes the new radius.  We update
	// local state but set prevRangeRef first so the search-trigger effect
	// above won't call setSearchParams again (preventing a loop).
	useEffect(() => {
		if (!isReady || !window.DDC?.LocationsData) {
			return () => {};
		}

		const locationsData = window.DDC.LocationsData;

		// Hydrate from LocationsData's current range (covers the case where
		// another widget changed the radius before this component mounted).
		if (typeof locationsData.getRange === 'function') {
			const currentRange = locationsData.getRange();
			if (typeof currentRange === 'number' && currentRange !== range) {
				prevRangeRef.current = currentRange;
				setRange(currentRange);
			}
		}

		// Subscribe to future radius changes from other widgets
		if (
			typeof locationsData.registerRadiusChangedNotification !==
			'function'
		) {
			return () => {};
		}

		const unsubscribe = locationsData.registerRadiusChangedNotification(
			(data) => {
				const newRadius = data?.radius;
				if (typeof newRadius === 'number' && newRadius !== range) {
					prevRangeRef.current = newRadius;
					setRange(newRadius);
				}
			}
		);

		return unsubscribe;
	}, [isReady, range]);

	const sitemap = useSitemap();
	const rawLocationDetailPageUrl = sitemap.get('LOCATION_DETAILS');
	const locationDetailPagePart = (rawLocationDetailPageUrl || '').slice(
		0,
		-4
	);

	const noResults =
		!isLoading &&
		isReady &&
		(locationCount === 0 ||
			!allLocationData ||
			allLocationData.length === 0);

	const tileData = buildLocationTiles(
		paginationData.paginatedData,
		locationDetailPagePart,
		dealerPhotoBaseUrl
	);

	return {
		isLoading,
		errorMsg,
		noResults,
		tileData,
		locationCount,
		allLocationData,
		showMap,
		range,
		setRange,
		// Reset triggers for pagination:
		// - range: user changed distance filter
		// - locationCount: underlying data changed (e.g. search cleared from ws-locations-control)
		// - showMap excluded — it's a display toggle, not a search change
		resetTriggers: [range, locationCount]
	};
};
